import ClaimsOperationIcon, {
	Sign,
} from 'components/commons/ClaimIcons/ClaimsOperationIcon.component';
import {
	calculateEstimates,
	setCalculated,
	setOtherServices,
	setStatusCalculation,
	setValidationErrors,
} from 'feature/claims/claimSlice';
import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import {
	BoxOverflow,
	Button,
	Stack,
	Table,
	TableBody,
	TableCell as TableCellUnstyled,
	TableHead,
	TableRow,
	Typography,
} from 'styles';
import { allBenefits, IClaim, IClaimComponents } from 'types/Claim';
import {
	formatPrice,
	getRangeValueAmount,
	roundingPrice100,
	serviceIdToLabel,
	useGetIsOtherComponent,
} from 'utils/utils';

import { useClaimHook } from 'hooks/claim.hook';
import useDevice from 'hooks/DesktopDevice.hook';
import { uniqueId } from 'lodash';
import styled from 'styled-components';
import InputNumber from 'styles/input/InputNumber';
import { breakpoints } from 'styles/theme/Breakpoints';
import palette from 'styles/theme/Palette';
import { IAmountLimit } from 'types/Member';
import { useTranslation } from 'utils/translations';
import OtherItemsSection from './OtherItemsSection';
import StandardItemsSection from './StandardItemsSection';

type prescriptionProps = {
	formData: IClaim;
	setFormData: (changeFunction: (prevValue: IClaim) => IClaim) => void;
	status: 'beforeCalculation' | 'afterCalculation' | 'onEdit';
	showGross?: boolean;
};

const PriceEstimate: FC<prescriptionProps> = props => {
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: 'clientLabels',
	});
	const { t: tErrorLabels } = useTranslation('', {
		keyPrefix: 'errors',
	});
	const dispatch = useDispatch<AppDispatch>();
	const { isTabletLandScape } = useDevice();

	const { formData, status, showGross = true } = props;
	const amount = formData.amount;

	const [tempAmounts, setTempAmounts] = useState<IClaim['amount']>(amount);

	const [error, setError] = useState<{
		grossAmount: boolean;
		maxValue: boolean;
		retailNotTaxAndNet: boolean;
		taxBiggerNet: boolean;
	}>({
		grossAmount: false,
		maxValue: false,
		retailNotTaxAndNet: false,
		taxBiggerNet: false,
	});
	const isThereErrors =
		error.grossAmount ||
		error.maxValue ||
		error.retailNotTaxAndNet ||
		error.taxBiggerNet;

	const [benefit, setBenefit] = useState<
		{ amountLimits: IAmountLimit[]; serviceId: number }[]
	>([]);
	const calculated = useSelector((state: RootState) => state.claim.calculated);
	const { member } = useSelector((state: RootState) => state.member);

	const { showTaxColumns } = useClaimHook();

	const { otherServices, validationErrors } = useSelector(
		(state: RootState) => state.claim,
	);

	const { isOtherComponent } = useGetIsOtherComponent();

	useEffect(() => {
		const otherServices =
			member?.benefit?.others.map(el => ({
				serviceLabel: el.serviceLabel,
				serviceId: el.serviceId,
			})) || [];

		dispatch(setOtherServices(otherServices));
	}, [dispatch, member]);

	useEffect(() => {
		if (member) {
			const benefits = [
				...(member?.benefit ? member.benefit.exam : []),
				...(member?.benefit ? member.benefit.materials : []),
				...(member?.benefit ? member.benefit.others : []),
			];
			const newBenefit = benefits.map(el => ({
				amountLimits: el.amountLimits,
				serviceId: el.serviceId,
			}));
			setBenefit(newBenefit);
		}
	}, [member]);

	useEffect(() => {
		//se initial value
		dispatch(
			setStatusCalculation(
				formData.amount.components.find(el => el.retailAmount)
					? 'afterCalculation'
					: 'beforeCalculation',
			),
		);
	}, [dispatch, formData.amount.components]);

	useEffect(() => {
		if (calculated) {
			dispatch(setStatusCalculation('afterCalculation'));
			dispatch(setCalculated(false));
		}
	}, [calculated, dispatch, amount]);

	useEffect(() => {
		return () => {
			dispatch(setValidationErrors([]));
		};
	}, [dispatch]);

	const resetTempAmounts = useCallback(
		(amounts: IClaim['amount']) => {
			setTempAmounts(() => ({
				...amounts,
				components: amounts.components.map(el => {
					if (
						otherServices.map(otherEl => otherEl.serviceId).includes(el.serviceId)
					) {
						return {
							serviceId: el.serviceId,
							retailAmount: el.retailAmount,
							retailNetOfTax: showTaxColumns ? el.retailNetOfTax : '',
							tax: showTaxColumns ? el.tax : '',
							grossRetail: showGross ? el.grossRetail : 0,
							rowId: uniqueId(),
						};
					}
					return {
						serviceId: el.serviceId,
						retailAmount: el.retailAmount,
						retailNetOfTax: showTaxColumns ? el.retailNetOfTax : '',
						tax: showTaxColumns ? el.tax : '',
						grossRetail: showGross ? el.grossRetail : 0,
					};
				}),
				total: {
					serviceId: null,
					retailAmount: '',
					retailNetOfTax: '',
					tax: '',
					grossRetail: 0,
				},
				accumulator: undefined,
			}));
		},
		[otherServices, showGross, showTaxColumns],
	);

	useEffect(() => {
		if (['onEdit', 'beforeCalculation'].includes(status)) {
			resetTempAmounts(amount);
		} else {
			setTempAmounts(amount);
		}
	}, [amount, status, resetTempAmounts]);

	const handleChangeAmounts = (
		formattedValue: string,
		serviceId: number | null,
		key: string,
	) => {
		const newComponents = tempAmounts.components.map(el => {
			if (el.serviceId === serviceId) {
				return {
					...el,
					[key]: formattedValue,
				};
			}

			return el;
		});

		const newTempAmounts = {
			...tempAmounts,
			components: newComponents,
		};

		setTempAmounts(newTempAmounts);

		const errorsCalculate = newComponents.reduce(
			(sum, el) => {
				const errorsRow = checkIsError(el, true);
				return {
					grossAmount: errorsRow.grossAmount || sum.grossAmount,
					maxValue: errorsRow.maxValue || sum.maxValue,
					retailNotTaxAndNet: errorsRow.retailNotTaxAndNet || sum.retailNotTaxAndNet,
					taxBiggerNet: errorsRow.taxBiggerNet || sum.taxBiggerNet,
				};
			},
			{
				grossAmount: false,
				maxValue: false,
				retailNotTaxAndNet: false,
				taxBiggerNet: false,
			},
		);
		setError(prev => ({
			grossAmount: prev.grossAmount && errorsCalculate.grossAmount,
			maxValue: prev.maxValue && errorsCalculate.maxValue,
			retailNotTaxAndNet:
				prev.retailNotTaxAndNet && errorsCalculate.retailNotTaxAndNet,
			taxBiggerNet: prev.taxBiggerNet && errorsCalculate.taxBiggerNet,
		}));
	};

	const addCurrency = (value: number | string | undefined) => {
		return ['afterCalculation'].includes(status) ? formatPrice(value ?? '') : '';
	};

	const handleCalculate = () => {
		const { components } = tempAmounts;

		const errorsCalculate = components.reduce(
			(sum, el) => {
				const errorsRow = checkIsError(el, true);
				return {
					grossAmount: errorsRow.grossAmount || sum.grossAmount,
					maxValue: errorsRow.maxValue || sum.maxValue,
					retailNotTaxAndNet: errorsRow.retailNotTaxAndNet || sum.retailNotTaxAndNet,
					taxBiggerNet: errorsRow.taxBiggerNet || sum.taxBiggerNet,
				};
			},
			{
				grossAmount: false,
				maxValue: false,
				retailNotTaxAndNet: false,
				taxBiggerNet: false,
			},
		);

		setError(errorsCalculate);
		if (
			!errorsCalculate.grossAmount &&
			!errorsCalculate.maxValue &&
			!errorsCalculate.retailNotTaxAndNet &&
			!errorsCalculate.taxBiggerNet
		) {
			const newComponents = tempAmounts.components.filter(el => el.serviceId); //to ignore empty rows

			const newTempAmounts = {
				...tempAmounts,
				components: newComponents,
			};

			setTempAmounts(newTempAmounts);

			dispatch(calculateEstimates({ ...formData, amount: newTempAmounts }));
		}
	};

	const checkIsError = (row: IClaimComponents, forceChecks?: boolean) => {
		const rowError = {
			grossAmount: false,
			maxValue: false,
			retailNotTaxAndNet: false,
			taxBiggerNet: false,
		};
		if (showGross && (error.grossAmount || forceChecks)) {
			rowError.grossAmount =
				roundingPrice100(row.grossRetail || '') <
				roundingPrice100(row.retailAmount || '');
		}
		if (error.maxValue || forceChecks) {
			rowError.maxValue =
				roundingPrice100(row.grossRetail || '') >
				roundingPrice100(
					getRangeValueAmount(row.serviceId, benefit, 'gross-retail-amount').max ||
						0,
				);
		}

		if (['onEdit'].includes(status) && showTaxColumns) {
			if (error.retailNotTaxAndNet || forceChecks) {
				rowError.retailNotTaxAndNet =
					roundingPrice100(row.retailNetOfTax) + roundingPrice100(row.tax) !==
					roundingPrice100(row.retailAmount);
			}
			if (error.taxBiggerNet || forceChecks) {
				rowError.taxBiggerNet =
					roundingPrice100(row.tax) > roundingPrice100(row.retailNetOfTax);
			}
		}

		return rowError;
	};

	const totals = tempAmounts.total;
	const otherComponents = tempAmounts.components.filter(el =>
		isOtherComponent(el),
	);

	//validations errors could be repeated. we want to filter there repetitions
	const validationErrorsTexts = validationErrors.map((err, idx) => {
		return tErrorLabels(err.message, {
			service: tClientLabels(
				serviceIdToLabel(
					allBenefits.find(benefit => benefit.value === Number(err.field))
						?.value as number,
				) as string,
			),
			feeSchedule: formatPrice(err.value),
			feeSchedulePercentageError: err.value,
		});
	});

	const validationErrorsFiltered = validationErrorsTexts.filter(
		(el, index) => validationErrorsTexts.indexOf(el) === index,
	);

	return (
		<Stack>
			<BoxOverflow
				variant="x"
				sx={{
					paddingBottom:
						(validationErrorsFiltered.length > 0 || isThereErrors) &&
						isTabletLandScape
							? '10px'
							: (validationErrorsFiltered.length > 0 || isThereErrors) &&
								  !isTabletLandScape
								? '20px'
								: '5px',
					marginBottom:
						validationErrorsFiltered.length > 0 || isThereErrors
							? 0
							: isTabletLandScape
								? '10px'
								: '20px',
				}}
			>
				<Stack
					sx={{
						minWidth: 1620,
					}}
				>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell sx={{ minWidth: '180px' }} $firstInRow>
									<Typography variant="bodyLink" sx={{ pl: '10px' }}>
										{tClientLabels('newClaim.priceEstimate.visionProduct')}
									</Typography>
								</TableCell>
								{showGross ? (
									<>
										<TableCell align="center">
											<Typography variant="bodyLink">
												{tClientLabels('newClaim.priceEstimate.grossRetail')}
											</Typography>
										</TableCell>
										<TableCell align="center" $smallIcon>
											<ClaimsOperationIcon sign={Sign.MINUS} />
										</TableCell>
										<TableCell align="center">
											<Typography variant="bodyLink">
												{tClientLabels('newClaim.priceEstimate.inStorePromotion')}
											</Typography>
										</TableCell>
										<TableCell align="center" $smallIcon>
											<ClaimsOperationIcon sign={Sign.EQUAL} />
										</TableCell>
									</>
								) : null}
								<TableCell align="center">
									<Typography variant="bodyLink">
										{tClientLabels('newClaim.priceEstimate.retailAmount')}
									</Typography>
								</TableCell>
								{showTaxColumns ? (
									<>
										<TableCell align="center" $smallIcon>
											<ClaimsOperationIcon sign={Sign.ARROW} />
										</TableCell>
										<TableCell align="center">
											<Typography variant="bodyLink">
												{tClientLabels('newClaim.priceEstimate.retailNetOfTax')}
											</Typography>
										</TableCell>
										<TableCell align="center" $smallIcon>
											<ClaimsOperationIcon sign={Sign.AND} />
										</TableCell>
										<TableCell align="center">
											<Typography variant="bodyLink">
												{tClientLabels('newClaim.priceEstimate.tax')}
											</Typography>
										</TableCell>
									</>
								) : (
									<></>
								)}
								<TableCell align="center" $smallIcon>
									<ClaimsOperationIcon sign={Sign.MINUS} />
								</TableCell>
								<TableCell align="center">
									<Typography variant="bodyLink">
										{tClientLabels('newClaim.priceEstimate.contractualAdjustments')}
									</Typography>
								</TableCell>
								<TableCell align="center" $smallIcon>
									<ClaimsOperationIcon sign={Sign.EQUAL} />
								</TableCell>
								<TableCell align="center">
									<Typography variant="bodyLink">
										{tClientLabels('newClaim.priceEstimate.memberPay')}
									</Typography>
								</TableCell>
								<TableCell align="center" $smallIcon>
									<ClaimsOperationIcon sign={Sign.PLUS} />
								</TableCell>
								<TableCell align="center">
									<Typography variant="bodyLink">
										{tClientLabels('newClaim.priceEstimate.planPay')}
									</Typography>
								</TableCell>
								{['beforeCalculation', 'onEdit'].includes(status) &&
								tempAmounts.components.filter(el => isOtherComponent(el)).length > 0 ? (
									<TableCell
										align="center"
										sx={{
											backgroundColor: theme => theme.palette.grey6.main,
											position: 'sticky',
											right: 0,
											width: 44,
										}}
									></TableCell>
								) : null}
							</TableRow>
						</TableHead>
						<TableBody>
							<StandardItemsSection
								error={error}
								tempAmounts={tempAmounts}
								setTempAmounts={setTempAmounts}
								checkIsError={checkIsError}
								handleChangeAmounts={handleChangeAmounts}
								isOtherComponent={isOtherComponent}
								addCurrency={addCurrency}
								showGross={showGross}
								status={status}
								validationErrors={validationErrors}
								showTaxColumns={showTaxColumns}
							/>
							<OtherItemsSection
								error={error}
								tempAmounts={tempAmounts}
								setTempAmounts={setTempAmounts}
								checkIsError={checkIsError}
								handleChangeAmounts={handleChangeAmounts}
								isOtherComponent={isOtherComponent}
								addCurrency={addCurrency}
								showTaxColumns={showTaxColumns}
								status={status}
							/>
							{!['beforeCalculation', 'onEdit'].includes(status) && ( //final row of totals
								<TableRow sx={{ height: { sm: 56, lg: 70 } }}>
									<TableCell sx={{ width: { sm: 136, lg: 230 } }}>
										<Typography variant="bodyLink" sx={{ pl: '10px' }}>
											{tClientLabels('newClaim.priceEstimate.totals')}:
										</Typography>
									</TableCell>
									{showGross ? (
										<>
											<TableCell align="center">
												<Typography variant="bodyLink">
													{addCurrency(totals.grossRetail)}
												</Typography>
											</TableCell>
											<TableCell align="center" $smallIcon></TableCell>
											<TableCell align="center">
												<Typography variant="bodyLink">
													{addCurrency(totals.inStorePromotion)}
												</Typography>
											</TableCell>
											<TableCell align="center" $smallIcon></TableCell>
										</>
									) : null}
									<TableCell align="center">
										<Typography variant="bodyLink">
											{addCurrency(totals.retailAmount)}
										</Typography>
									</TableCell>
									<TableCell align="center" $smallIcon></TableCell>
									{showTaxColumns ? (
										<>
											<TableCell align="center">
												<Typography variant="bodyLink">
													{addCurrency(totals.retailNetOfTax)}
												</Typography>
											</TableCell>
											<TableCell align="center" $smallIcon></TableCell>
											<TableCell align="center">
												<Typography variant="bodyLink">
													{addCurrency(totals.tax)}
												</Typography>
											</TableCell>
											<TableCell align="center" $smallIcon></TableCell>
										</>
									) : (
										<></>
									)}
									<TableCell align="center">
										<Typography variant="bodyLink">
											{addCurrency(totals.contractualAdjustmentAmount)}
										</Typography>
									</TableCell>
									<TableCell align="center" $smallIcon>
										<Typography variant="bodyLink" style={{ color: palette.grey2.main }}>
											{addCurrency(totals.discountedAmount)}
										</Typography>
									</TableCell>
									<TableCell align="center">
										<Typography variant="bodyLink">
											{addCurrency(totals.memberPayAmount)}
										</Typography>
									</TableCell>
									<TableCell align="center" $smallIcon></TableCell>
									<TableCell align="center">
										<Typography variant="bodyLink">
											{addCurrency(totals.allowablePlanPay)}
										</Typography>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</Stack>
				{(validationErrorsFiltered.length > 0 || isThereErrors) && (
					<Stack mt={otherComponents.length > 0 ? '20px' : 0}>
						{validationErrorsFiltered.map((err, idx) => (
							<Typography key={idx} variant="bodyLink" color={palette.error.main}>
								{err}
							</Typography>
						))}
						{error.grossAmount && (
							<Typography variant="bodyLink" color={palette.error.main}>
								{tClientLabels('newClaim.priceEstimate.grossRetailError')}
							</Typography>
						)}
						{error.maxValue && (
							<Typography variant="bodyLink" color={palette.error.main}>
								{tClientLabels('newClaim.priceEstimate.maxAmountError')}
							</Typography>
						)}
						{error.retailNotTaxAndNet && (
							<Typography variant="bodyLink" color={palette.error.main}>
								{tClientLabels('newClaim.priceEstimate.retailNotTaxAndNetError')}
							</Typography>
						)}
						{error.taxBiggerNet && (
							<Typography variant="bodyLink" color={palette.error.main}>
								{tClientLabels('newClaim.priceEstimate.taxBiggerNetError')}
							</Typography>
						)}
					</Stack>
				)}
			</BoxOverflow>
			{['beforeCalculation', 'onEdit'].includes(status) ? (
				<Stack
					direction={'row'}
					gap="20px"
					style={{
						marginTop:
							otherComponents.length > 0 ||
							validationErrorsFiltered.length > 0 ||
							isThereErrors
								? '20px'
								: 0,
					}}
				>
					{['onEdit'].includes(status) ? (
						<Button
							variant="outlined"
							onClick={() => {
								dispatch(setStatusCalculation('afterCalculation'));
								setTempAmounts(amount);
								setError({
									grossAmount: false,
									maxValue: false,
									retailNotTaxAndNet: false,
									taxBiggerNet: false,
								});
							}}
						>
							{tClientLabels('commons.buttons.cancel')}
						</Button>
					) : null}
					<Button
						disabled={Boolean(
							tempAmounts.components.find(
								el =>
									el.serviceId && (!el.retailAmount || (!el.grossRetail && showGross)),
							),
						)}
						variant="contained"
						onClick={handleCalculate}
					>
						{tClientLabels('newClaim.priceEstimate.calculate')}
					</Button>
				</Stack>
			) : null}
		</Stack>
	);
};

type TableCellProps = typeof TableCellUnstyled & {
	$smallIcon?: boolean;
	$firstInRow?: boolean;
};

export const TableCell = styled(TableCellUnstyled)<TableCellProps>`
	&.MuiTableCell-head {
		padding: ${({ $smallIcon }) =>
			$smallIcon ? '8px 0px 8px 0px' : '8px 0px 8px 20px'};
		width: ${({ $smallIcon }) => ($smallIcon ? '24px' : 'auto')};
	}
	padding: ${({ $smallIcon }) =>
		$smallIcon ? '8px 0px 8px 0px' : '8px 0px 8px 20px'};
	@media (min-width: ${breakpoints.values.sm}px) {
		&.MuiTableCell-head {
			padding: ${({ $smallIcon, $firstInRow }) =>
				$smallIcon
					? '8px 0px 8px 0px'
					: $firstInRow
						? '8px 8px 8px 10px'
						: '8px 16px 8px 16px'};
			width: ${({ $smallIcon }) => ($smallIcon ? '24px' : 'auto')};
		}
		padding: ${({ $smallIcon }) =>
			$smallIcon ? '8px 0px 8px 0px' : '8px 8px 8px 10px'};
	}
`;

export const OutlinedInput = styled(InputNumber)`
	width: 91px;
	height: 28px;
	border-radius: 4px;
	padding-right: 8px;
	input {
		text-align: right;
		margin-right: 0px;
		margin-bottom: 16px;
		font-weight: 400;
		font-size: 13px;
	}
	input::-webkit-outer-spin-button,
	input::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}
	input[type='text'] {
		appearance: none;
		-moz-appearance: textfield;
	}
	.MuiInputBase-root,
	.MuiInputBase-root:hover,
	.MuiInputBase-root:focus {
		background-color: transparent !important;
	}
`;

export default PriceEstimate;
